import React, { useEffect, useState } from 'react';
import { Zone, ZoneLayoutType } from '../../XV2';
import { UiImage, UiFlex, UiText, UiButton } from '@open-stack/ui';
import { OrderPaymentProgressBar } from '../order/payment-progress-bar/OrderPaymentProgressBar';
import { useMenuProductCategory } from '../../hooks/menu-product-category';
import { useZone } from '../../hooks/zone';
import { XOverlay } from '../x/overlay/XOverlay';
import { useZoneLastVisited } from '../../hooks/zone-last-visited';
import { OrderProductFormDialog } from '../order/product/form-overlay/OrderProductFormDialog';
import { BrowsePage } from '../../pages/browse/BrowsePage';
import { useZoneHistory } from '../../hooks/zone-history';
import { CovidRegistrationOverlay } from '../covid/registration-overlay/CovidRegistrationOverlay';
import { XAlert, XAlertType } from '../x/alert/XAlert';
import { XText, XTextType } from '../x/text/XText';
import { useActiveOrder } from '../../hooks/active-order';
import { useToggle } from '../../hooks/toggle';
import { SpcCheckout } from '../../spc/checkout/SpcCheckout';
import { OrderCheckout } from '../order/checkout/OrderCheckout';
import { XBox, XButton, XSection, useRouter } from '@orderx/lab/ui';
import { ZoneViewMenuLayoutByCategory } from './menu-layout/by-category/ZoneViewMenuLayoutByCategory';
import { ZoneViewMenuLayoutDefault } from './menu-layout/default/ZoneViewMenuLayoutDefault';
import { useStyle } from '../../hooks/useStyle';
import './ZoneView.scss';
import { LocationPage } from '../../pages/location/LocationPage';
import { useOnResume } from '../../hooks/on-resume';
import { toastShowSuccess } from '@orderx/lab/xr/toast';

interface Props {
    zone: Zone;
}

export function ZoneView({ zone }: Props) {
    const $zone = useZone();
    const { response } = $zone;
    const zoneHistory = useZoneHistory();
    const { setZoneLastVisited, reset: resetLastVisitedZone } = useZoneLastVisited();
    const menuProductCategory = useMenuProductCategory();
    const activeOrder = useActiveOrder();
    const { hasActiveOrder, orderProducts, removeOrderProductById } = activeOrder;
    const [isCheckoutOpen, toggleIsCheckoutOpen] = useToggle();
    const router = useRouter();
    const [lastActivity, setLastActivity] = useState(Date.now());

    $zone.useRegister(zone.id);
    activeOrder.useRegister(zone.outletId);

    useOnResume(() => {
        setLastActivity(Date.now());
    }, []);

    useEffect(() => {
        setLastActivity(Date.now());

        return () => {
            menuProductCategory.reset();
        };
    }, []);

    useEffect(() => {
        if (hasActiveOrder && $zone.menuProducts) {
            const menuProductIds = new Set($zone.menuProducts.map((mp) => mp.productId));

            for (const orderProduct of orderProducts) {
                if (!menuProductIds.has(orderProduct.productId)) {
                    removeOrderProductById(orderProduct.id);
                }
            }
        }
    }, [hasActiveOrder, $zone.menuProducts]);

    useEffect(() => {
        if (response) {
            setZoneLastVisited({
                zoneId: response.zone.id,
                zoneHash: response.zone.hash,
                zoneName: response.zone.name,
                outletName: response.outlet.name,
                locationName: response.location.name,
            });
        }
    }, [response]);

    useOnResume(() => {
        const redirect = $zone.response?.idleState?.redirect;

        if (redirect?.afterXMinutes && redirect.afterXMinutes > 0 && lastActivity) {
            const secondsIdle = Math.round((Date.now() - lastActivity) / 1000);
            const minutesIdle = Math.round(secondsIdle / 60);

            if (minutesIdle > redirect.afterXMinutes) {
                resetLastVisitedZone();

                toastShowSuccess(redirect.message || 'Please choose your table again');

                router.push([
                    `${LocationPage.route}?outletId=${response.outlet.id}`,
                    { locationId: response.location.id },
                ]);
            }
        }
    }, [lastActivity, $zone.response]);

    useStyle(response?.style);

    useEffect(() => {
        if (hasActiveOrder) {
            menuProductCategory.setCategory('MY_ORDER');
        }

        activeOrder.resetPlacedOrderId();
    }, []);

    useEffect(() => {
        if (!hasActiveOrder && menuProductCategory.category === 'MY_ORDER') {
            menuProductCategory.setCategory('');
        }
    }, [menuProductCategory.category, hasActiveOrder]);

    const menu = response?.menu || [];

    if (!response) {
        return <XOverlay type="loading" />;
    }

    const {
        name,
        image,
        outlet,
        isClosed,
        payments: { total, paid, isPaymentRequired },
        isQueueLimitReached,
    } = response;

    const { isCovidRegistrationRequired, isCovidRegistrationRequiredUpfront } = outlet;

    let isProductsDisplayed = !isQueueLimitReached && !isClosed && menu.length > 0;

    const outletHistory = zoneHistory.getHistory(zone.id);

    if (response?.zone?.isPayMode) {
        isProductsDisplayed = false;
    }

    return (
        <>
            {(isCovidRegistrationRequired || isCovidRegistrationRequiredUpfront) && (
                <CovidRegistrationOverlay outlet={outlet} zone={zone} tagData={$zone.response.covidRegistration} />
            )}

            <div className={'ZoneView'}>
                <div className="toolbar">
                    {hasActiveOrder ? (
                        <XButton
                            onClick={menuProductCategory.toggleActiveOrder}
                            color={'secondary'}
                            className={'my-order'}
                            icon={menuProductCategory.isMyOrderActive() ? 'close' : ''}>
                            <XBox flex gap={1}>
                                <span>{menuProductCategory.isMyOrderActive() ? 'Close' : 'My order'}</span>
                                {!menuProductCategory.isMyOrderActive() && (
                                    <span className={'product-count'}>{activeOrder.getTotalProductCount()}</span>
                                )}
                            </XBox>
                        </XButton>
                    ) : (
                        <UiButton
                            to={outletHistory ? outletHistory.value : BrowsePage.route}
                            icon="arrow_back"
                            className="button-back"
                            color="body"
                        />
                    )}
                </div>

                {!$zone.isLayoutTypeGroupedByCategory() && (
                    <>
                        <div className="image-container">
                            <UiImage src={image} type="cover" />
                        </div>

                        <div className="image-placeholder" />
                    </>
                )}

                <div className="outlet-content">
                    <UiText className={'outlet-name'} color={'text-secondary'} align={'center'} display={'block'}>
                        {outlet.name}
                    </UiText>

                    <UiFlex className={'zone-name'} justifyContent={'center'}>
                        <XText type={XTextType.TITLE}>{name}</XText>
                    </UiFlex>

                    {isPaymentRequired && (
                        <XSection>
                            <OrderPaymentProgressBar amountTotal={total} amountPaid={paid}>
                                <XButton onClick={toggleIsCheckoutOpen} color="primary" fullWidth>
                                    Pay
                                </XButton>

                                {isCheckoutOpen && <SpcCheckout onClose={toggleIsCheckoutOpen} />}
                            </OrderPaymentProgressBar>
                        </XSection>
                    )}

                    {isClosed && (
                        <XSection>
                            <XAlert type={XAlertType.WARNING}>
                                <XText>We are currently closed</XText>
                            </XAlert>
                        </XSection>
                    )}

                    {isQueueLimitReached && (
                        <XSection>
                            <XAlert type={XAlertType.WARNING}>
                                Our staff is at full capacity! We will let new orders through as soon as we're able to
                                make them. Please try again in a while...
                            </XAlert>
                        </XSection>
                    )}

                    {isProductsDisplayed && (
                        <>
                            {($zone.isLayoutTypeGroupedByCategory() && <ZoneViewMenuLayoutByCategory />) || (
                                <ZoneViewMenuLayoutDefault />
                            )}
                        </>
                    )}
                </div>
            </div>

            {!isQueueLimitReached && <OrderCheckout />}

            <OrderProductFormDialog />
        </>
    );
}
